'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

const navLinks = [
  { id: 'nav_home', label: 'Home', href: '/' },
  { id: 'nav_services', label: 'Services', href: '/services' },
  { id: 'nav_portfolio', label: 'Portfolio', href: '/portfolio' },
  { id: 'nav_news', label: 'News', href: '/news' },
  { id: 'nav_about', label: 'About', href: '/about' },
];

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const pathname = usePathname();
  const toggleRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);

  const isActive = (href: string) => pathname === href;

  /**
   * Scroll state drives only a background/border change — never a height or
   * padding change, so the header can never resize or bounce while scrolling.
   * Passive listener + an equality guard keeps this off the render path.
   */
  useEffect(() => {
    const onScroll = () => {
      const scrolled = window.scrollY > 8;
      setIsScrolled((prev) => (prev === scrolled ? prev : scrolled));
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const closeMenu = useCallback(() => setIsMobileMenuOpen(false), []);

  // Close the mobile menu on route change.
  useEffect(() => {
    closeMenu();
  }, [pathname, closeMenu]);

  // Escape to close, plus background scroll lock while the panel is open.
  useEffect(() => {
    if (!isMobileMenuOpen) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeMenu();
        toggleRef.current?.focus();
      }
    };

    const { body } = document;
    const prevOverflow = body.style.overflow;
    body.style.overflow = 'hidden';

    document.addEventListener('keydown', onKeyDown);
    const focusTimer = setTimeout(() => {
      panelRef.current?.querySelector<HTMLElement>('a')?.focus();
    }, 0);

    return () => {
      clearTimeout(focusTimer);
      document.removeEventListener('keydown', onKeyDown);
      body.style.overflow = prevOverflow;
    };
  }, [isMobileMenuOpen, closeMenu]);

  return (
    <>
      <header
        className={`fixed top-0 left-0 w-full z-50 h-[var(--header-height)] transition-[background-color,border-color,box-shadow] duration-base ease-out ${
          isScrolled ? 'glass border-b shadow-xs' : 'bg-background border-b border-transparent'
        }`}
      >
        <nav
          aria-label="Primary"
          className="container-page h-full flex items-center justify-between gap-6"
        >
          <Link
            href="/"
            className="flex items-center rounded-sm transition-opacity duration-fast hover:opacity-70"
            aria-label="Micratto — home"
          >
            <AppImage
              src="/assets/images/micratto-logo.svg"
              alt="Micratto"
              width={100}
              height={27}
              className="object-contain bg-transparent max-w-full h-auto"
              priority
              isLogo
            />
          </Link>

          {/* Desktop navigation */}
          <div className="hidden md:flex items-center gap-7">
            {navLinks.map((link) => (
              <Link
                key={link.id}
                href={link.href}
                aria-current={isActive(link.href) ? 'page' : undefined}
                className={`group/nav relative py-1 text-[0.84375rem] font-medium tracking-[0.02em] transition-colors duration-fast ${
                  isActive(link.href)
                    ? 'text-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {link.label}
                {/* Always rendered, only scaled — no layout shift. Hover shows a
                    faint preview of the underline; active shows it fully. */}
                <span
                  aria-hidden="true"
                  className={`pointer-events-none absolute -bottom-0.5 left-0 h-[2px] w-full origin-left rounded-full bg-accent transition-[transform,opacity] duration-base ease-out ${
                    isActive(link.href)
                      ? 'scale-x-100 opacity-100'
                      : 'scale-x-0 opacity-40 group-hover/nav:scale-x-100'
                  }`}
                />
              </Link>
            ))}
            <Link
              href="/contact"
              className="inline-flex h-9 items-center rounded-md bg-primary px-4 text-[0.8125rem] font-semibold text-primary-foreground shadow-[inset_0_1px_0_rgba(255,255,255,0.08)] transition-[background-color,box-shadow,transform] duration-fast hover:bg-[#26344d] hover:shadow-md active:translate-y-px motion-reduce:active:transform-none"
            >
              Contact
            </Link>
          </div>

          {/* Mobile toggle */}
          <button
            ref={toggleRef}
            type="button"
            onClick={() => setIsMobileMenuOpen((v) => !v)}
            className="md:hidden -mr-2 flex h-11 w-11 items-center justify-center rounded-md text-foreground transition-colors duration-fast hover:bg-muted"
            aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={isMobileMenuOpen}
            aria-controls="mobile-menu"
          >
            <Icon name={isMobileMenuOpen ? 'XMarkIcon' : 'Bars3Icon'} size={22} />
          </button>
        </nav>
      </header>

      {/* `hidden` when closed keeps these links out of the tab order entirely —
          a panel merely translated off-screen stays focusable. */}
      <div
        id="mobile-menu"
        ref={panelRef}
        hidden={!isMobileMenuOpen}
        className="fixed inset-0 z-40 bg-background md:hidden"
      >
        <div className="flex h-full flex-col overflow-y-auto overscroll-contain px-6 pb-10 pt-[calc(var(--header-height)+1.5rem)]">
          <nav aria-label="Mobile" className="flex flex-col">
            {navLinks.map((link, i) => (
              <Link
                key={link.id}
                href={link.href}
                onClick={closeMenu}
                aria-current={isActive(link.href) ? 'page' : undefined}
                style={{ animationDelay: `${i * 35}ms` }}
                className={`rise flex min-h-[52px] items-center justify-between border-b text-lg font-semibold transition-colors duration-fast ${
                  isActive(link.href) ? 'text-accent-strong' : 'text-foreground'
                }`}
              >
                {link.label}
                {isActive(link.href) && (
                  <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    Current
                  </span>
                )}
              </Link>
            ))}
          </nav>

          <Link
            href="/contact"
            onClick={closeMenu}
            className="press mt-8 inline-flex min-h-[52px] items-center justify-center rounded-md bg-primary px-6 text-base font-semibold text-primary-foreground transition-colors duration-fast hover:bg-primary/90"
          >
            Contact Us
          </Link>
        </div>
      </div>
    </>
  );
};

export default Header;
